// src/components/PublicTimelineTable.js
import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    TablePagination,
    Paper,
    Typography,
    CircularProgress,
    Alert,
    // Tooltip, // No longer explicitly used here, but could be added back if needed
} from '@mui/material';
import { visuallyHidden } from '@mui/utils'; // For accessibility with sorting
import CaseDetailModal from './CaseDetailModal'; // Import the modal
import FollowInfoBanner from './FollowInfoBanner'; // Import the new banner component
import AlertPortal from './AlertPortal';
import axiosInstance from '../api/axiosInstance'; // For API calls
import { useAuth } from '../context/AuthContext'; // Correctly import useAuth hook

// Helper function to format dates (adjust format as needed) - Export for use in Modal
export const formatDate = (dateString) => {
    // Handle null, undefined, or empty string immediately
    if (!dateString) {
        return 'N/A';
    }

    try {
        const date = new Date(dateString);
        const timestamp = date.getTime();

        // Check for invalid date (isNaN) or the specific Unix epoch timestamp (0),
        // which often results from parsing null/undefined implicitly.
        if (isNaN(timestamp) || timestamp === 0) {
            return 'N/A';
        }

        // Format valid dates to YYYY-MM-DD
        return date.toISOString().split('T')[0];
    } catch (e) {
        // Safeguard against unexpected errors during Date parsing
        console.error("Error formatting date:", dateString, e);
        return 'N/A';
    }
};

// Helper function for stable sorting
function stableSort(array, comparator) {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
        const order = comparator(a[0], b[0]);
        if (order !== 0) {
            return order;
        }
        return a[1] - b[1]; // Use original index for stability
    });
    return stabilizedThis.map((el) => el[0]);
}

// Helper function to get the comparator
function getComparator(order, orderBy) {
    return order === 'desc'
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}

// Helper function for descending comparison (handles dates and nulls)
function descendingComparator(a, b, orderBy) {
    let valA = a[orderBy];
    let valB = b[orderBy];

    // Handle date comparisons specifically - treat nulls as earliest
    const dateFields = ['submissionTimestamp', 'itaDate', 'aorDate', 'portal1Date', 'portal2Date', 'finalDecisionDate', 'eCoprDate'];
    if (dateFields.includes(orderBy)) {
        // Treat null/undefined/invalid dates as very old dates for sorting purposes
        const dateA = valA ? new Date(valA).getTime() : -Infinity;
        const dateB = valB ? new Date(valB).getTime() : -Infinity;

        if (isNaN(dateA) && isNaN(dateB)) return 0;
        if (isNaN(dateB)) return -1; // Put invalid B dates first when descending
        if (isNaN(dateA)) return 1;  // Put invalid A dates first when descending

        if (dateB < dateA) return -1;
        if (dateB > dateA) return 1;
        return 0;
    }

    // Fallback for non-date fields (though primarily dates are sorted here)
    // Treat null/undefined as less than actual values
    if (valB == null && valA != null) return -1;
    if (valA == null && valB != null) return 1;
    if (valA == null && valB == null) return 0;

    if (valB < valA) return -1;
    if (valB > valA) return 1;
    return 0;
}


// --- Component Definition ---

// Added selectedStreams prop
const PublicTimelineTable = ({ data, isLoading, error, selectedStreams, selectedVOs, userTimelineId }) => {
    const { user, fetchUser } = useAuth(); // Use the useAuth hook and get fetchUser

    const [order, setOrder] = useState('desc'); // Default sort: latest submitted first
    const [orderBy, setOrderBy] = useState('aorDate'); // Default sort field (changed from submissionTimestamp)
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10); // Or 25, etc.
    const [filteredData, setFilteredData] = useState([]);

    // Modal State
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedCase, setSelectedCase] = useState(null);
    const [isFollowingSelectedCase, setIsFollowingSelectedCase] = useState(false);
    const [isProcessingFollow, setIsProcessingFollow] = useState(false);

    // Snackbar state for feedback
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success'); // 'success' or 'error'


    // Define table columns
    const headCells = [
        { id: 'applicationStream', numeric: false, disablePadding: false, label: 'Stream', sortable: false }, // Not typically sorted
        { id: 'itaDate', numeric: false, disablePadding: false, label: 'ITA', sortable: true },
        { id: 'aorDate', numeric: false, disablePadding: false, label: 'AOR', sortable: true },
        { id: 'portal1Date', numeric: false, disablePadding: false, label: 'P1', sortable: true },
        { id: 'portal2Date', numeric: false, disablePadding: false, label: 'P2', sortable: true },
        { id: 'finalDecisionDate', numeric: false, disablePadding: false, label: 'FD', sortable: true },
        { id: 'eCoprDate', numeric: false, disablePadding: false, label: 'eCoPR', sortable: true },
        // Add other relevant columns if needed, e.g., VO Office
        { id: 'voOffice', numeric: false, disablePadding: false, label: 'VO', sortable: false },
    ];

     // Filter data based on selectedStreams and selectedVOs whenever data or filters change
     useEffect(() => {
         if (data && Array.isArray(data)) {
             const newlyFilteredData = data.filter(item =>
                 (selectedStreams.length === 0 || selectedStreams.includes(item.applicationStream)) &&
                 (selectedVOs.length === 0 || selectedVOs.includes(item.voOffice))
             );
             setFilteredData(newlyFilteredData);
             setPage(0); // Reset page when filter changes
         } else {
             setFilteredData([]); // Handle case where data is null/not an array
         }
     }, [data, selectedStreams, selectedVOs]);


    const handleRequestSort = (event, property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Avoid a layout jump when reaching the last page with empty rows.
    const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - filteredData.length) : 0;

    // Memoize the sorted rows to avoid re-sorting on every render
    const visibleRows = useMemo(() =>
        stableSort(filteredData, getComparator(order, orderBy))
            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
        [filteredData, order, orderBy, page, rowsPerPage] // Recalculate when these change
    );

    const handleRowClick = (caseItem) => {
        setSelectedCase(caseItem);
        if (user && user.followingTimelines && caseItem?._id) {
            setIsFollowingSelectedCase(user.followingTimelines.includes(caseItem._id));
        } else {
            setIsFollowingSelectedCase(false);
        }
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedCase(null);
        setIsFollowingSelectedCase(false); // Reset on close
    };

    const handleFollowToggle = async (timelineId, currentlyFollowing) => {
        console.log('[handleFollowToggle] Called. Timeline ID:', timelineId, 'Currently Following:', currentlyFollowing, 'User:', user);

        if (!user) {
            console.log('[handleFollowToggle] User not logged in.');
            setSnackbarMessage('You must be logged in to follow cases.');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            return;
        }
        if (!timelineId) {
            console.log('[handleFollowToggle] No Timeline ID provided.');
            setSnackbarMessage('Error: Timeline ID missing. Cannot process request.');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            return;
        }

        console.log('[handleFollowToggle] Proceeding with API call. Setting isProcessingFollow to true.');
        setIsProcessingFollow(true);
        const endpoint = currentlyFollowing
            ? `/api/user/timelines/unfollow/${timelineId}` // Added /api prefix
            : `/api/user/timelines/follow/${timelineId}`;   // Added /api prefix
        console.log('[handleFollowToggle] Endpoint:', endpoint);

        try {
            console.log('[handleFollowToggle] Making API call...');
            const response = await axiosInstance.post(endpoint);
            console.log('[handleFollowToggle] API response:', response);

            if (response.data && response.data.user) {
                console.log('[handleFollowToggle] Success. Updating context and UI.');
                await fetchUser(); // Call fetchUser to update the context
                setIsFollowingSelectedCase(!currentlyFollowing); // Toggle state
                setSnackbarMessage(response.data.message || `Case ${currentlyFollowing ? 'unfollowed' : 'followed'} successfully.`);
                setSnackbarSeverity('success');
            } else {
                console.warn('[handleFollowToggle] API response missing expected data:', response.data);
                throw new Error(response.data?.error || 'Unexpected response structure from server.');
            }
        } catch (err) {
            console.error('[handleFollowToggle] API Error:', err.response || err.message || err);
            setSnackbarMessage(err.response?.data?.error || err.message || `Failed to ${currentlyFollowing ? 'unfollow' : 'follow'} case.`);
            setSnackbarSeverity('error');
        } finally {
            console.log('[handleFollowToggle] Finally block. Setting isProcessingFollow to false. Snackbar should open.');
            setIsProcessingFollow(false);
            setSnackbarOpen(true); // Ensure snackbar always attempts to open
        }
    };

    const handleCloseSnackbar = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setSnackbarOpen(false);
    };


    // --- Render Logic ---

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 3 }}>
                <CircularProgress />
                <Typography sx={{ ml: 2 }}>Loading timeline data...</Typography>
            </Box>
        );
    }

    if (error) {
        return <Alert severity="error" sx={{ mt: 2 }}>Error loading timeline data: {error}</Alert>;
    }

    if (!data || data.length === 0) {
        return <Typography sx={{ textAlign: 'center', p: 2, mt: 1 }}>No public timeline data submitted yet.</Typography>;
    }

     if (filteredData.length === 0 && (selectedStreams.length > 0 || selectedVOs.length > 0)) {
        return <Typography sx={{ textAlign: 'center', p: 2, mt: 1 }}>No public timeline data matches the selected filters.</Typography>;
    }

    return (
        <React.Fragment>
            <Box sx={{ width: '100%' }}> {/* Wrap in a Box to contain Banner and Paper */}
                <FollowInfoBanner />
                <Paper sx={{ width: '100%', mb: 2 }}>
                    <TableContainer>
                        <Table
                            sx={{ minWidth: 750 }} // Adjust minWidth as needed
                            aria-labelledby="tableTitle"
                            size={'small'} // Use 'small' size for compactness
                        >
                            <TableHead>
                                <TableRow>
                                    {headCells.map((headCell) => (
                                        <TableCell
                                            key={headCell.id}
                                            align={headCell.numeric ? 'right' : 'left'}
                                            padding={headCell.disablePadding ? 'none' : 'normal'}
                                            sortDirection={orderBy === headCell.id ? order : false}
                                            sx={{ fontWeight: 'bold' }} // Make headers bold
                                        >
                                            {headCell.sortable ? (
                                                <TableSortLabel
                                                    active={orderBy === headCell.id}
                                                    direction={orderBy === headCell.id ? order : 'asc'}
                                                    onClick={(event) => handleRequestSort(event, headCell.id)}
                                                >
                                                    {headCell.label}
                                                    {orderBy === headCell.id ? (
                                                        <Box component="span" sx={visuallyHidden}>
                                                            {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                                                        </Box>
                                                    ) : null}
                                                </TableSortLabel>
                                            ) : (
                                                headCell.label
                                            )}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {visibleRows.map((row, index) => {
                                    // You might want a more stable key if available, like row._id from backend
                                    const labelId = `public-timeline-table-checkbox-${index}`;
                                    const isFollowedByCurrentUser = user && user.followingTimelines && row._id && user.followingTimelines.includes(row._id);
                                    const isOwnTimeline = userTimelineId && row._id === userTimelineId;

                                    return (
                                        <TableRow
                                            hover
                                            role="listitem" // Changed from checkbox for semantics
                                            tabIndex={-1}
                                            key={row._id || labelId} // Use MongoDB _id if available
                                            sx={{
                                                cursor: 'pointer',
                                                ...(isOwnTimeline
                                                    ? { backgroundColor: 'rgba(255, 235, 59, 0.3)' } // Light yellow for own timeline
                                                    : isFollowedByCurrentUser
                                                    ? { backgroundColor: '#e3f2fd' } // Light blue for followed rows
                                                    : {}),
                                            }}
                                            onClick={() => handleRowClick(row)} // Make row clickable
                                        >
                                            {/* Render cells based on headCells definition */}
                                            <TableCell align="left">{row.applicationStream || 'N/A'}</TableCell>
                                            <TableCell align="left">{formatDate(row.itaDate)}</TableCell>
                                            <TableCell align="left">{formatDate(row.aorDate)}</TableCell>
                                            <TableCell align="left">{formatDate(row.portal1Date)}</TableCell>
                                            <TableCell align="left">{formatDate(row.portal2Date)}</TableCell>
                                            <TableCell align="left">{formatDate(row.finalDecisionDate)}</TableCell>
                                            <TableCell align="left">{formatDate(row.eCoprDate)}</TableCell>
                                            <TableCell align="left">{row.voOffice || 'N/A'}</TableCell>
                                        </TableRow>
                                    );
                                })}
                                {emptyRows > 0 && (
                                    <TableRow style={{ height: 33 * emptyRows }}>
                                        <TableCell colSpan={headCells.length} />
                                    </TableRow>
                                )}
                                {/* Add row if visibleRows is empty after filtering/pagination */}
                                {visibleRows.length === 0 && filteredData.length > 0 && (
                                    <TableRow>
                                        <TableCell colSpan={headCells.length} align="center">
                                            No results found on this page.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25, 50]}
                        component="div"
                        count={filteredData.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        sx={{
                            // Target the internal Toolbar component to apply flexbox alignment
                            '& .MuiTablePagination-toolbar': {
                                alignItems: 'center',
                            },
                        }}
                    />

                    {selectedCase && (
                        <CaseDetailModal
                            open={isModalOpen}
                            onClose={handleCloseModal}
                            caseData={selectedCase}
                            onFollowToggle={handleFollowToggle}
                            isFollowing={isFollowingSelectedCase}
                            isProcessingFollow={isProcessingFollow}
                            isOwnTimeline={selectedCase?._id === userTimelineId}
                        />
                    )}

                </Paper>
            </Box>
            <AlertPortal
                open={snackbarOpen}
                message={snackbarMessage}
                severity={snackbarSeverity}
                onClose={handleCloseSnackbar}
            />
        </React.Fragment>
    );
};

export default PublicTimelineTable;